import React, { useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

/**
 * About page component
 * Follows top-down architecture by respecting App.js layout constraints
 */
const About = () => {
  // Refs for section title and card animations
  const sectionRefs = useRef([]);
  const cardRefs = useRef([]);

  // Intersection Observer for section title and card animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in-view');
            // Once animated, stop observing to improve scroll performance
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px' // Trigger slightly before element enters viewport
      }
    );

    // Copy refs to variables to avoid stale closures in cleanup
    const currentSectionRefs = sectionRefs.current;
    const currentCardRefs = cardRefs.current;

    // Observe section titles
    currentSectionRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    // Observe cards
    currentCardRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentSectionRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
      currentCardRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // useEffect(() => {
  //   const el = scrollRef.current;
  //   if (!el) return;

  //   let lastY = el.scrollTop;  // Store the previous scroll position
  //   let accumulatedScroll = 0;  // Track total accumulated scroll

  //   const handleScroll = () => {
  //     const currentY = el.scrollTop;
  //     const delta = currentY - lastY;  // Calculate scroll distance for this event

  //     if (Math.abs(delta) > 10) { // Only consider significant scroll changes
  //       accumulatedScroll += delta;  // Add delta to the accumulated scroll
  //       // console.log(`📦 Scroll  by ${Math.abs(delta)}px`);

  //       // Pass the accumulated scroll to App.js for navbar control
  //       if (onScrollChange) {
  //         onScrollChange(accumulatedScroll); // Pass accumulated scroll value
  //       }
        
  //       lastY = currentY;  // Update last scroll position
  //     }
  //   };

  //   el.addEventListener("scroll", handleScroll, { passive: true });

  //   return () => el.removeEventListener("scroll", handleScroll);
  // }, [onScrollChange]);



  return (
    <>
      <Helmet>
        <title>About me - limkc.com</title>
        <meta name="description" content="Learn About me." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      

      <div className="w-full h-full overflow-auto">
        <div
          // ref={scrollRef}
          className="about-page-container px-4 py-6 transition-all duration-300 min-h-screen"
          style={{
            paddingTop: "calc(var(--navbar-height) + 1.5rem)",
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
          }}
        >
          {/* Magazine-style Hero with Portrait Photo */}
          <section className="about-hero-section">
            <div className="about-hero-grid">
              {/* Portrait Photo */}
              <div className="about-photo-container">
                <img
                  src="https://static.limkc.com/IMG_2864.JPG"
                  alt="LimKC"
                  className="about-photo-img"
                />
                <div className="about-photo-overlay">
                  <h1 className="about-overlay-title">About Me</h1>
                </div>
              </div>

              {/* About Text */}
              <div className="about-text-content">
                <h1 className="text-4xl font-bold mb-6 about-desktop-title" style={{ color: 'var(--text-primary)' }}>About Me</h1>
                <p className="about-hero-paragraph mb-6" style={{ color: 'var(--text-primary)' }}>
                  Weekends might find me chasing the perfect shot with a camera, getting lost in creating animations, or finding restaurants that serve the best food you've never heard of. You might find me editing videos and cutting film, traveling somewhere new, or at a nearby mountain top meditating. I tinker constantly, whether it's hardware circuits or software systems. Curiosity not only kills cats, I'm sure it steals most of my time. I just get lost in it. That same curiosity led to a PhD exploring optimization algorithms and control system theory for underground mining energy systems. Now I work on cloud infrastructure and AI systems, but the approach is the same: experiment, iterate, understand the constraints and break through all of them.
                </p>
              </div>
            </div>
          </section>

          {/* Rest of About Content */}
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <h2 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Professional Evolution</h2>
          
          <p className="mb-6" style={{ color: 'var(--text-primary)' }}>
            Started in the depths of underground mines, designing embedded system frameworks for explosion proof battery management systems. This experience taught me about architecture design where mistakes matter. That foundation evolved into building cloud infrastructure and machine learning systems.
          </p>

          <h2 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Technical Expertise</h2>

          <p className="mb-4" style={{ color: 'var(--text-primary)' }}><strong>Cloud & IoT Infrastructure:</strong></p>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            Built multi-tenant IoT platform for device monitoring, telemetry visualization, and firmware deployment. Global device tracking with region/site mapping, 3D surface plots for telemetry data, OTA firmware updates. Designed for massive battery data at scale with infrastructure supporting future AI analysis of telemetry patterns.
          </p>

          <p className="mb-4" style={{ color: 'var(--text-primary)' }}><strong>Machine Learning & AI Systems:</strong></p>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            Built this site's chat system with vision, voice, image generation, and tool calling capabilities. Explored SLM deployment on edge devices (Jetson, Pi with NPU acceleration). Experimented with computer vision using Intel RealSense depth cameras and voice synthesis. Working with retrieval systems for regulatory compliance where text chunking can split critical specifications across chunks. These explorations led to current focus on production language model systems and infrastructure.
          </p>

          <h2 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Philosophy</h2>
          
          <p className="mb-6" style={{ color: 'var(--text-primary)' }}>
            The best solutions come from understanding constraints. Embedded systems taught me that every design decision has consequences. Memory constraints in microcontrollers, context windows in language models, balancing cost and latency in production systems. The interesting problems are about these choices. Every limitation is a design parameter. Every constraint forces creativity.
          </p>

          <p className="mb-6" style={{ color: 'var(--text-primary)' }}>
            <em>From battery algorithms to cloud infrastructure to machine learning systems.</em>
          </p>

          {/* Core Expertise Section */}
          <section className="about-expertise-section">
            <div className="section-header section-title-slide-left" ref={(el) => (sectionRefs.current[0] = el)}>
              <h2 className="text-4xl font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>Core Expertise</h2>
              <p className="mb-6 text-center" style={{ color: 'var(--text-secondary)' }}>
                Specialized skills in cloud architecture, AI automation, and modern DevOps practices.
              </p>
            </div>

            <div className="feature-grid">
            <div className="feature-card card-slide-up" ref={(el) => (cardRefs.current[0] = el)}>
              <div className="feature-icon">
                <img src="/cloud.png" alt="Cloud Architecture" className="feature-icon-img" />
              </div>
              <h3 className="feature-title">Cloud Architecture</h3>
              <p className="feature-description">
                Designing and implementing scalable, resilient multi-cloud infrastructure using AWS, Azure, and GCP with cost optimization strategies.
              </p>
            </div>

            <div className="feature-card card-slide-up" ref={(el) => (cardRefs.current[1] = el)}>
              <div className="feature-icon">
                <img src="/generative.png" alt="AI Automation" className="feature-icon-img" />
              </div>
              <h3 className="feature-title">AI Automation</h3>
              <p className="feature-description">
                Building intelligent automation systems with machine learning, natural language processing, and AI-driven decision-making capabilities.
              </p>
            </div>

            <div className="feature-card card-slide-up" ref={(el) => (cardRefs.current[2] = el)}>
              <div className="feature-icon">
                <img src="/hosting.png" alt="Serverless Computing" className="feature-icon-img" />
              </div>
              <h3 className="feature-title">Serverless Computing</h3>
              <p className="feature-description">
                Expert in AWS Lambda, API Gateway, and event-driven architectures. Building cost-effective, auto-scaling serverless applications.
              </p>
            </div>

            <div className="feature-card card-slide-up" ref={(el) => (cardRefs.current[3] = el)}>
              <div className="feature-icon">
                <img src="/cargo-ship.png" alt="Kubernetes & Containers" className="feature-icon-img" />
              </div>
              <h3 className="feature-title">Kubernetes & Containers</h3>
              <p className="feature-description">
                Orchestrating containerized applications with Kubernetes, Docker, and cloud-native technologies for maximum portability.
              </p>
            </div>

            <div className="feature-card card-slide-up" ref={(el) => (cardRefs.current[4] = el)}>
              <div className="feature-icon">
                <img src="/cloud-computing.png" alt="Database Management" className="feature-icon-img" />
              </div>
              <h3 className="feature-title">Database Management</h3>
              <p className="feature-description">
                Architecting scalable database solutions with DynamoDB, RDS, MongoDB, and Redis. Expertise in data modeling and optimization.
              </p>
            </div>

            <div className="feature-card card-slide-up" ref={(el) => (cardRefs.current[5] = el)}>
              <div className="feature-icon">
                <img src="/secure-data.png" alt="Security & IAM" className="feature-icon-img" />
              </div>
              <h3 className="feature-title">Security & IAM</h3>
              <p className="feature-description">
                Implementing enterprise-grade security with AWS IAM, encryption, and zero-trust architecture principles.
              </p>
            </div>
          </div>
          </section>

          {/* Services & Solutions Section */}
          <section className="about-services-section">
            <div className="section-header section-title-slide-right" ref={(el) => (sectionRefs.current[1] = el)}>
              <h2 className="text-4xl font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>Services & Solutions</h2>
              <p className="mb-6 text-center" style={{ color: 'var(--text-secondary)' }}>
                Comprehensive cloud and AI solutions tailored to your business needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
              <div className="card-3d card-slide-from-left flex flex-col" ref={(el) => (cardRefs.current[6] = el)}>
                <span className="tag mb-md">Cloud Infrastructure</span>
                <h3 className="text-lg font-bold mb-sm">Multi-Cloud Architecture</h3>
                <p className="text-secondary mb-lg flex-grow">
                  Design and implement scalable, cost-optimized cloud infrastructure across AWS, Azure, and GCP with high availability and disaster recovery.
                </p>
                <Link to="/projects">
                  <button className="btn-primary w-full">Learn More</button>
                </Link>
              </div>

              <div className="card-3d card-slide-from-right flex flex-col" ref={(el) => (cardRefs.current[7] = el)}>
                <span className="tag mb-md">AI & Automation</span>
                <h3 className="text-lg font-bold mb-sm">AI-Driven Automation</h3>
                <p className="text-secondary mb-lg flex-grow">
                  Build intelligent chatbots, automated workflows, and AI-powered decision systems using cutting-edge machine learning technologies.
                </p>
                <Link to="/chatty">
                  <button className="btn-primary w-full">Try AI Assistant</button>
                </Link>
              </div>

              <div className="card-3d card-slide-from-left flex flex-col" ref={(el) => (cardRefs.current[8] = el)}>
                <span className="tag mb-md">Serverless</span>
                <h3 className="text-lg font-bold mb-sm">Serverless Applications</h3>
                <p className="text-secondary mb-lg flex-grow">
                  Develop event-driven, auto-scaling serverless architectures with AWS Lambda, API Gateway, and DynamoDB for maximum cost efficiency.
                </p>
                <Link to="/projects">
                  <button className="btn-primary w-full">Explore Serverless</button>
                </Link>
              </div>

              <div className="card-3d card-slide-from-right flex flex-col" ref={(el) => (cardRefs.current[9] = el)}>
                <span className="tag mb-md">DevOps</span>
                <h3 className="text-lg font-bold mb-sm">CI/CD & Infrastructure as Code</h3>
                <p className="text-secondary mb-lg flex-grow">
                  Implement automated deployment pipelines, infrastructure as code with Terraform/CloudFormation, and GitOps workflows.
                </p>
                <Link to="/projects">
                  <button className="btn-primary w-full">View DevOps</button>
                </Link>
              </div>
            </div>
          </section>

          {/* Technology Stack Section */}
          <section className="about-techstack-section">
            <div className="section-header section-title-slide-bottom" ref={(el) => (sectionRefs.current[2] = el)}>
              <h2 className="text-4xl font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>Technology Stack</h2>
              <p className="mb-6 text-center" style={{ color: 'var(--text-secondary)' }}>
                Modern tools and technologies powering scalable solutions.
              </p>
            </div>

            <div className="testimonial-grid">
              <div className="testimonial-card card-slide-up" ref={(el) => (cardRefs.current[10] = el)}>
                <h4 className="font-bold mb-sm">Cloud Platforms</h4>
                <p className="text-secondary">
                  AWS (Lambda, EC2, S3, DynamoDB, API Gateway, CloudFormation), Azure, Google Cloud Platform, Multi-Cloud Architecture
                </p>
              </div>

              <div className="testimonial-card card-slide-up" ref={(el) => (cardRefs.current[11] = el)}>
                <h4 className="font-bold mb-sm">Containers & Orchestration</h4>
                <p className="text-secondary">
                  Kubernetes, Docker, Amazon ECS/EKS, Helm, Container Registry, Service Mesh (Istio)
                </p>
              </div>

              <div className="testimonial-card card-slide-up" ref={(el) => (cardRefs.current[12] = el)}>
                <h4 className="font-bold mb-sm">AI & Machine Learning</h4>
                <p className="text-secondary">
                  OpenAI GPT, Anthropic Claude, TensorFlow, PyTorch, NLP, Computer Vision, LangChain, Vector Databases
                </p>
              </div>

              <div className="testimonial-card card-slide-up" ref={(el) => (cardRefs.current[13] = el)}>
                <h4 className="font-bold mb-sm">DevOps & Automation</h4>
                <p className="text-secondary">
                  Terraform, Ansible, GitHub Actions, CI/CD Pipelines, GitOps, Infrastructure as Code, Monitoring (CloudWatch, Prometheus)
                </p>
              </div>

              <div className="testimonial-card card-slide-up" ref={(el) => (cardRefs.current[14] = el)}>
                <h4 className="font-bold mb-sm">Programming & Frameworks</h4>
                <p className="text-secondary">
                  Python, JavaScript/Node.js, React, TypeScript, Go, Bash Scripting, REST APIs, GraphQL
                </p>
              </div>

              <div className="testimonial-card card-slide-up" ref={(el) => (cardRefs.current[15] = el)}>
                <h4 className="font-bold mb-sm">Databases & Storage</h4>
                <p className="text-secondary">
                  DynamoDB, PostgreSQL, MongoDB, Redis, S3, ElasticSearch, Database Migration & Optimization
                </p>
              </div>
            </div>
          </section>
          </div>
        </div>
      </div>
      <style>{`
        .about-hero-section {
          max-width: 1200px;
          margin: 0 auto 4rem;
          padding: 2rem 0;
        }

        .about-expertise-section {
          max-width: 1200px;
          margin: 4rem auto 4rem;
          padding: 2rem 0;
        }

        .about-services-section {
          max-width: 1200px;
          margin: 4rem auto 4rem;
          padding: 2rem 0;
        }

        .about-techstack-section {
          max-width: 1200px;
          margin: 4rem auto 4rem;
          padding: 2rem 0;
        }

        .about-hero-grid {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 48px;
          align-items: start;
        }

        .about-photo-container {
          position: relative;
          overflow: hidden;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          aspect-ratio: 3/4;
        }

        .about-photo-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          object-position: center;
        }

        .about-photo-overlay {
          display: none;
        }

        .about-overlay-title {
          display: none;
        }

        .about-hero-paragraph {
          font-size: 1.125rem;
          line-height: 1.8;
        }

        @media (max-width: 768px) {
          .about-hero-paragraph {
            font-size: 1rem;
            line-height: 1.5;
          }
          #primary-main-content {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          .about-page-container {
            padding-left: 0 !important;
            padding-right: 0 !important;
            padding-top: var(--navbar-height) !important;
          }

          .about-hero-section {
            max-width: 100%;
            padding: 0;
            margin: 0 0 2rem;
          }

          .about-hero-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .about-photo-container {
            aspect-ratio: 16/9;
            border-radius: 0;
            margin: 0;
            width: 100vw;
            box-shadow: none;
          }

          .about-photo-img {
            object-position: center 35%;
          }

          .about-photo-overlay {
            display: block;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%);
            padding: 2rem 1rem 1rem;
          }

          .about-overlay-title {
            display: block;
            font-size: 2.5rem;
            font-weight: bold;
            color: white;
            margin: 0;
          }

          .about-desktop-title {
            display: none;
          }

          .about-hero-grid > div:nth-child(2) {
            padding: 0 1rem;
          }

          .about-page-container > div:not(.about-hero-section) {
            padding: 0 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default About;